"use client"
import "./RegisterForm.css"
import { useState } from "react"
import axios from "../utils/axios"
import taaounityLogo from "../logos/taaounity.png"
import { User, Mail, Lock, Eye, EyeOff, Smartphone } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { GoogleLogin } from '@react-oauth/google'
import Notification from "./Notification"
import { trackMetaCompleteRegistration } from "../utils/metaPixel"


export default function RegisterForm({ language, translations }) {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  })

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [notification, setNotification] = useState({ show: false, message: "", type: "error" })

  const navigate = useNavigate()

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value })

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (formData.password !== formData.confirmPassword) {
      setNotification({
        show: true,
        message: translations?.passwords_no_match?.[language] || "Passwords do not match",
        type: "error"
      })
      return
    }

    if (!acceptTerms) {
      setNotification({
        show: true,
        message: translations?.accept_terms?.[language] || "Please accept the terms",
        type: "error"
      })
      return
    }

    const phoneTrimmed = (formData.phone || "").trim()
    if (!phoneTrimmed || phoneTrimmed.length < 8) {
      setNotification({
        show: true,
        message:
          translations?.phone_invalid?.[language] ||
          (language === "ar"
            ? "رقم الهاتف مطلوب (8 أحرف على الأقل)"
            : language === "fr"
              ? "Numéro de téléphone requis (8 caractères minimum)"
              : "Phone number is required (at least 8 characters)"),
        type: "error",
      })
      return
    }

    setIsLoading(true)

    try {
      const response = await axios.post("/api/auth/register/", {
        username: formData.username,
        email: formData.email,
        phone: phoneTrimmed,
        password: formData.password,
      })

      // Store JWT tokens in localStorage
      if (response.data.access) {
        localStorage.setItem('access_token', response.data.access);
        localStorage.setItem('refresh_token', response.data.refresh);
        localStorage.setItem('username', formData.username);
        
        // Set default authorization header for future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access}`;
        
        // Newly registered users are owners (not team members)
        localStorage.setItem("is_team_member", "false");
        localStorage.removeItem("member_data");
      }
      trackMetaCompleteRegistration('form');
      setNotification({
        show: true,
        message: translations?.register_success?.[language] || "Registration successful!",
        type: "success"
      })
      setTimeout(() => navigate(response.data.needs_onboarding === false ? "/dashboard" : "/onboarding", { state: { success: "register_success" } }), 1000)
    } catch (err) {
      const errorMsg = translations?.register_error?.[language] || err.response?.data?.error || "Registration failed"
      setNotification({
        show: true,
        message: errorMsg,
        type: "error"
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    try {
      console.log('Google credential received:', credentialResponse);
      
      // Use the new Google One Tap endpoint
              const response = await axios.post("/api/auth/google-one-tap/", {
        id_token: credentialResponse.credential,
      });
  
      // Store JWT tokens in localStorage
      if (response.data.access) {
        localStorage.setItem('access_token', response.data.access);
        localStorage.setItem('refresh_token', response.data.refresh);
        
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access}`;
        
        localStorage.setItem("is_team_member", "false");
        localStorage.removeItem("member_data");
      }
  
      if (response.data.requires_phone) {
        localStorage.setItem("signup_source", "google_one_tap");
        navigate("/complete-profile");
        return;
      }

      if (response.data.is_new_user) {
        trackMetaCompleteRegistration('google');
      }

      navigate(response.data.needs_onboarding === false ? "/dashboard" : "/onboarding", { state: { success: "register_success" } });
    } catch (error) {
      console.error('Google login error:', error.response?.data || error)
      const backendDetail = error.response?.data?.detail
      const backendError = error.response?.data?.error
      let errorMsg =
        translations?.google_login_error?.[language] ||
        backendDetail ||
        backendError ||
        "Échec de la connexion Google"
      if (
        typeof window !== "undefined" &&
        window.location.hostname === "localhost" &&
        !backendDetail &&
        !backendError
      ) {
        errorMsg +=
          language === "fr"
            ? " — Vérifiez que http://localhost:3000 est autorisé dans Google Cloud Console."
            : language === "ar"
              ? " — تأكد من إضافة http://localhost:3000 في Google Cloud Console."
              : " — Ensure http://localhost:3000 is allowed in Google Cloud Console."
      }
      setNotification({
        show: true,
        message: errorMsg,
        type: "error"
      })
    }
  };

  return (
    <div className="register-page">
      <Notification
        show={notification.show}
        message={notification.message}
        type={notification.type}
        language={language}
        translations={translations}
        onClose={() => setNotification({ ...notification, show: false })}
      />
      <div className="registerform">
        <form onSubmit={handleSubmit} className="register-form">
          <div
            className={`register-brand${language === "ar" ? " register-brand--rtl" : ""}`}
          >
            <img
              src={taaounityLogo}
              alt=""
              className="register-logo"
              width={180}
              height={56}
            />
            <div className="register-brand-text">
              <h2 className="register-brand-title">Taaouniaty</h2>
              <span className="register-brand-tagline">Pro</span>
            </div>
          </div>
          {/* Username */}
          <div className="form-group">
            <div className="input-wrapper">
              <div className="input-icon">
                <User className="h-5 w-5" />
              </div>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder={language === 'ar' ? 'اسم المستخدم' : language === 'fr' ? "Nom d'utilisateur" : 'Username'}
                required
                className="form-input"
                autoComplete="username"
              />
            </div>
          </div>

          {/* Email */}
          <div className="form-group">
            <div className="input-wrapper">
              <div className="input-icon">
                <Mail className="h-5 w-5" />
              </div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={language === 'ar' ? 'البريد الإلكتروني' : language === 'fr' ? 'Adresse e-mail' : 'Email address'}
                required
                className="form-input"
                autoComplete="email"
              />
            </div>
          </div>

          {/* Phone */}
          <div className="form-group">
            <div className="input-wrapper">
              <div className="input-icon">
                <Smartphone className="h-5 w-5" />
              </div>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={
                  language === "ar"
                    ? "رقم الهاتف"
                    : language === "fr"
                      ? "Téléphone"
                      : "Phone number"
                }
                required
                className="form-input"
                autoComplete="tel"
              />
            </div>
          </div>

          {/* Password */}
          <div className="form-group">
            <div className="input-wrapper">
              <div className="input-icon">
                <Lock className="h-5 w-5" />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={language === 'ar' ? 'كلمة المرور' : language === 'fr' ? 'Mot de passe' : 'Password'}
                required
                className="form-input"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className="password-toggle"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="form-group">
            <div className="input-wrapper">
              <div className="input-icon">
                <Lock className="h-5 w-5" />
              </div>
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={language === 'ar' ? 'تأكيد كلمة المرور' : language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm password'}
                required
                className="form-input"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((p) => !p)}
                className="password-toggle"
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Terms */}
          <div className="checkbox-wrapper">
            <input
              id="accept-terms"
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              className="checkbox"
              
            />
            <label
              htmlFor="accept-terms"
              className="checkbox-label"
              style={language === 'ar' ? { whiteSpace: 'nowrap', direction: 'rtl' } : {}}
            >
              {language === 'ar' ? (
                <>
                  {'أوافق على '}
                  <a href="#" className="font-medium text-blue-600 hover:text-blue-500">{'الشروط'}</a>
                  {' و '}
                  <a href="#" className="font-medium text-blue-600 hover:text-blue-500">{'سياسة الخصوصية'}</a>
                </>
              ) : language === 'fr' ? (
                <>
                  {"J'accepte les "}
                  <a href="#" className="font-medium text-blue-600 hover:text-blue-500">Conditions</a>
                  {" et la "}
                  <a href="#" className="font-medium text-blue-600 hover:text-blue-500">Politique de confidentialité</a>
                  {'.'}
                </>
              ) : (
                <>
                  {'I accept the '}
                  <a href="#" className="font-medium text-blue-600 hover:text-blue-500">Terms</a>
                  {' and the '}
                  <a href="#" className="font-medium text-blue-600 hover:text-blue-500">Privacy Policy</a>
                  {'.'}
                </>
              )}
            </label>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading || !acceptTerms}
            className="submit-btn"
          >
            {isLoading ? (
              <span className="loading-text">
                <span className="spinner"></span>
                {language === 'ar' ? 'جارٍ إنشاء الحساب…' : language === 'fr' ? 'Création du compte…' : 'Creating account…'}
              </span>
            ) : (
              (language === 'ar' ? 'إنشاء حساب' : language === 'fr' ? 'Créer un compte' : 'Create account')
            )}
          </button>

          {/* Already have account */}
          <div className="form-footer">
            <p className="form-text">
              {language === 'ar' ? 'لديك حساب بالفعل؟' : language === 'fr' ? 'Vous avez déjà un compte ?' : 'Already have an account?'}{" "}
              <Link to="/login" className="form-link">{language === 'ar' ? 'تسجيل الدخول' : language === 'fr' ? 'Se connecter' : 'Sign in'}</Link>
            </p>
          </div>
          {/* Google Signup */}
          <div className="google-login-wrapper">
            <GoogleLogin
              onSuccess={handleGoogleLoginSuccess}
              onError={() => alert("Échec de la connexion Google")}
            />
          </div>
        </form>
      </div>
    </div>
  )
}
